import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Bookmark, BookmarkCheck, CheckCircle2, ChevronRight,
  Clock, Loader2, MapPin, MessageSquare, ShieldCheck, Star, Stamp, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import CheckinDialog from "@/components/app/CheckinDialog";
import StaffPanel from "@/components/app/StaffPanel";
import CampsiteEventStrip from "@/components/app/CampsiteEventStrip";

// Camper-facing detail page for a single campsite.
// Renders: photo header, name + verification chip, state + level + rating,
// facilities/description, Save toggle, "Check in here" CTA (opens
// CheckinDialog), and a paginated list of reviews under the fold.
export default function CampsiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, refresh } = useAuth();

  const [campsite, setCampsite] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCampsite(null); setReviews(null); setError(null);
    Promise.allSettled([
      api.get(`/campsites/${id}`),
      api.get(`/campsites/${id}/reviews`, { params: { limit: 10, offset: 0 } }),
    ]).then(([csRes, revRes]) => {
      if (cancelled) return;
      if (csRes.status === "fulfilled") setCampsite(csRes.value.data || null);
      else setError(csRes.reason?.response?.data?.detail || "Could not load this campsite.");
      if (revRes.status === "fulfilled") setReviews(revRes.value.data?.items || []);
      else setReviews([]);
    });
    return () => { cancelled = true; };
  }, [api, id]);

  const isSaved = useMemo(
    () => Boolean(user?.saved_campsites?.includes(id)),
    [user, id],
  );
  const isOwnCampsite = !!(campsite?.owner_id && campsite.owner_id === user?.user_id);
  const verified = campsite?.verification_status === "verified";

  const toggleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/campsites/${id}/save`);
      await refresh(); // saved_campsites is stored on the user doc
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not update saved list.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <div className="card-elev p-8">
          <div className="text-lg font-extrabold mb-2" style={{ color: "var(--on-surface)" }}>
            Something went wrong
          </div>
          <div className="text-sm mb-6" style={{ color: "var(--on-surface-muted)" }}>{error}</div>
          <button className="btn btn-green" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>
    );
  }

  if (!campsite) {
    return (
      <div className="max-w-3xl mx-auto py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="campsite-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="self-start text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
        style={{ color: "var(--brand)" }}
        data-testid="campsite-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      {/* Header photo */}
      <section className="card-elev overflow-hidden">
        <div className="aspect-[16/8] md:aspect-[21/8] bg-black/10 overflow-hidden">
          {campsite.photo ? (
            <img src={campsite.photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--on-surface-muted)" }}>
              <MapPin className="w-10 h-10 opacity-40" />
            </div>
          )}
        </div>
        <div className="p-5 md:p-7 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {verified && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}
                      data-testid="campsite-verified">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ background: "color-mix(in srgb, var(--premium) 12%, transparent)", color: "var(--premium)" }}>
                <Trophy className="w-3 h-3" /> Lvl {campsite.level ?? 1} · {campsite.title || "Rookie Site"}
              </span>
              {campsite.rating_avg != null && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: "color-mix(in srgb, var(--premium) 12%, transparent)", color: "var(--premium)" }}>
                  <Star className="w-3 h-3" fill="currentColor" />
                  {Number(campsite.rating_avg).toFixed(1)} · {campsite.rating_count || 0} reviews
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--on-surface)" }} data-testid="campsite-name">
              {campsite.name}
            </h1>
            <div className="mt-1 text-sm flex items-center gap-1" style={{ color: "var(--on-surface-muted)" }}>
              <MapPin className="w-3.5 h-3.5" />
              {[campsite.address, campsite.state, campsite.country || "Malaysia"].filter(Boolean).join(", ")}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={toggleSave}
              disabled={saving}
              className="btn btn-light"
              data-testid="campsite-save-toggle"
              title={isSaved ? "Remove from saved" : "Save for later"}
            >
              {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              {isSaved ? "Saved" : "Save"}
            </button>
            {!isOwnCampsite && (
              <button
                onClick={() => setDialogOpen(true)}
                className="btn btn-green"
                data-testid="campsite-checkin-cta"
              >
                <Stamp className="w-4 h-4" /> Check in here
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Description / facilities */}
      {(campsite.description || campsite.facilities?.length) && (
        <section className="card-elev p-6" data-testid="campsite-about">
          <h2 className="text-lg font-extrabold mb-3" style={{ color: "var(--on-surface)" }}>About this campsite</h2>
          {campsite.description && (
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--on-surface)" }}>
              {campsite.description}
            </p>
          )}
          {campsite.facilities?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="campsite-facilities">
              {campsite.facilities.map((f) => (
                <span key={f} className="text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                      style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
                  {f}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Reviews */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>
            Reviews {reviews && ` · ${reviews.length}`}
          </h2>
          {isOwnCampsite && (
            <Link to="/owner" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
                  style={{ color: "var(--brand)" }}>
              Manage <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
        {reviews == null && (
          <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reviews…
          </div>
        )}
        {reviews && reviews.length === 0 && (
          <div className="card-elev p-6 text-sm" style={{ color: "var(--on-surface-muted)" }} data-testid="campsite-reviews-empty">
            No written reviews yet. Be the first to check in and share your experience.
          </div>
        )}
        {reviews && reviews.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
            {reviews.map((r) => <ReviewCard key={r.checkin_id} r={r} />)}
          </div>
        )}
      </section>

      <CampsiteEventStrip campsiteId={id} isOwnCampsite={isOwnCampsite} />

      {/* Owner-only staff panel — inline invite/list/remove.
          `isPrimaryOwner` is true when the viewer is the campsite's
          `owner_id`, not a staff member — matches the backend gate on
          `POST /staff/invite` / `DELETE /staff/{uid}`. */}
      <StaffPanel campsite={campsite} isPrimaryOwner={isOwnCampsite} />

      {dialogOpen && (
        <CheckinDialog
          campsite={campsite}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <div className="card-elev p-4 flex flex-col gap-2" data-testid={`review-${r.checkin_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {r.avatar_data ? (
            <img src={r.avatar_data} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-extrabold"
                 style={{ background: "var(--brand)", color: "white" }}>
              {(r.display_name?.[0] || "C").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" style={{ color: "var(--on-surface)" }}>{r.display_name}</div>
            <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>
              {(r.checkin_date || "").slice(0, 10)}
              {r.camper_level && ` · Lvl ${r.camper_level}`}
            </div>
          </div>
        </div>
        {r.rating != null && (
          <div className="shrink-0 inline-flex items-center gap-1 text-sm font-extrabold" style={{ color: "var(--premium)" }}>
            <Star className="w-4 h-4" fill="currentColor" /> {Number(r.rating).toFixed(1)}
          </div>
        )}
      </div>
      {r.review_body && (
        <p className="text-sm leading-relaxed" style={{ color: "var(--on-surface)" }}>{r.review_body}</p>
      )}
    </div>
  );
}
